import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('barevon:pair-media-preview', this.#handlePairPreview, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #previewController;
  #selectionVersion = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#beginSelection();
  }

  // Native selections and pair previews share one clock; neither may overwrite a newer action.
  #beginSelection() {
    this.#previewController?.abort();
    this.#previewController = undefined;
    return ++this.#selectionVersion;
  }

  /** @param {MediaGallery} gallery @param {'first'|'second'} pair */
  #applyGallery(gallery, pair) {
    const featuredMediaId = gallery.dataset.featuredMediaId;
    const sameMedia = this.dataset.colorMediaIds &&
      this.dataset.productId === gallery.dataset.productId &&
      this.dataset.colorMediaKey === gallery.dataset.colorMediaKey &&
      this.dataset.colorMediaIds === gallery.dataset.colorMediaIds &&
      this.dataset.presentation === gallery.dataset.presentation;

    if (sameMedia) {
      // Keep a browsed photo on size-only changes unless that variant has a different featured photo.
      if (featuredMediaId && featuredMediaId !== this.dataset.featuredMediaId) {
        const index = (this.dataset.mediaIds || '').split(',').indexOf(featuredMediaId);
        if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
      }
      this.dataset.variantId = gallery.dataset.variantId || '';
      this.dataset.featuredMediaId = featuredMediaId || '';
      this.dataset.previewPair = pair;
      return;
    }

    const index = (gallery.dataset.mediaIds || '').split(',').indexOf(featuredMediaId || '');
    if (index >= 0) gallery.querySelector('slideshow-component')?.setAttribute('initial-slide', String(index));
    gallery.dataset.previewPair = pair;
    this.replaceWith(gallery);
  }

  /**
   * Preview only the requested pair's media. This never emits a commerce event or changes a form/URL.
   * @param {CustomEvent} event
   */
  #handlePairPreview = async (event) => {
    const detail = event.detail;
    if (!(event.target instanceof Element) || !event.target.closest('barevon-bogo, barevon-studio-offer') ||
      String(detail?.productId) !== this.dataset.productId ||
      !['first', 'second'].includes(detail?.pair) || !/^\d+$/.test(String(detail?.variantId))) return;

    const version = this.#beginSelection();
    if (String(detail.variantId) === this.dataset.variantId) {
      this.dataset.previewPair = detail.pair;
      return;
    }
    if (!this.dataset.productUrl || !this.dataset.sectionId) return;
    const url = new URL(this.dataset.productUrl, window.location.origin);
    if (url.origin !== window.location.origin) return;
    url.searchParams.delete('option_values');
    url.searchParams.set('variant', String(detail.variantId));
    url.searchParams.set('section_id', this.dataset.sectionId);
    const view = new URLSearchParams(window.location.search).get('view');
    if (view) url.searchParams.set('view', view);
    this.#previewController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#previewController.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Gallery preview returned ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!this.isConnected || version !== this.#selectionVersion) return;
      const gallery = Array.from(html.querySelectorAll('media-gallery')).find(
        (element) => element.dataset.productId === this.dataset.productId &&
          element.dataset.sectionId === this.dataset.sectionId &&
          element.dataset.variantId === String(detail.variantId)
      );
      if (gallery) this.#applyGallery(/** @type {MediaGallery} */ (gallery), detail.pair);
    } catch (error) {
      if (error?.name !== 'AbortError' && this.isConnected && version === this.#selectionVersion) {
        console.warn('[media-gallery] Pair preview failed:', error);
      }
    }
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const version = this.#beginSelection();

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected || version !== this.#selectionVersion) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.#applyGallery(newMediaGallery, 'first');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
